import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { useFormik } from 'formik';
import * as s from './styled-user-form';
import { removeInvalidChars } from '../../utils';
import { yupMessages } from '../../constants';

// Modal de Cadastro/Edicao de Usuario — design Usuarios.html (handoff).
// Hairline shell: Scrim + Modal + head/body/foot. Substitui o
// ModalShell antigo. Mantem props originais (currentCustomers,
// formikInitialValues, telaConcluido, etc) e Formik+yup.

// ============================================================
// Icones inline (Lucide-style) — sem dependencia npm
// ============================================================
const IClose = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const ICheck = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const parseEmpresaValue = (value) => {
  const normalizedValue = String(value || '');
  const separatorIndex = normalizedValue.lastIndexOf('-');

  if (separatorIndex === -1) {
    return { id: normalizedValue, type: 'customer' };
  }

  return {
    id: normalizedValue.slice(0, separatorIndex),
    type: normalizedValue.slice(separatorIndex + 1),
  };
};

export default ({
  id = 'modal',
  onClose,
  onCloseConfirmation,
  submit,
  title,
  dadosAbaixo,
  sucesso,
  iconFinal,
  telaConcluido,
  setConfirmationModal,
  setTelaConcluido,
  currentCustomers,
  msgButton,
  labelButton,
  formikInitialValues,
  disabled,
  // Quando true, exibe campos de nova senha (opcional) — usado na edicao
  // pra um admin definir a senha manualmente (sem depender do "esqueci a
  // senha"). Em branco = mantem a senha atual.
  showPasswordFields = false,
}) => {
  const { user } = useSelector((state) => state.usuario);

  // isCompanySelect = "Higra" toggle ON → usuario interno (vinculado
  // a company), nao ao customer. Mantem nome da prop pra compat com
  // o submit/parent. Inicial: true se ja vier company=true do edit.
  const [isCompanySelect, setIsCompanySelect] = useState(
    !!formikInitialValues.company,
  );

  // Lista de papeis depende do contexto:
  // - user.company (Higra/empresa): pode criar admin + outros
  // - user.customer (cliente final): so operador/visualizador
  const listPapel = user.company
    ? [
        { label: 'Admin Local', value: 'admin' },
        { label: 'Operador', value: 'operador' },
        { label: 'Visualizador', value: 'visualizador' },
      ]
    : [
        { label: 'Operador', value: 'operador' },
        { label: 'Visualizador', value: 'visualizador' },
      ];

  const formik = useFormik({
    initialValues: formikInitialValues,
    onSubmit: () => {},
    validationSchema: yup.object({
      name: yup.string().required(yupMessages.REQUIRED),
      email: yup
        .string()
        .email(yupMessages.EMAIL_INVALID)
        .required(yupMessages.REQUIRED),
      empresa: yup.string(),
      company: yup.boolean(),
      papel: yup.string().required(yupMessages.REQUIRED),
    }),
  });

  // empresa = id do customer escolhido no select. Quando isCompanySelect
  // ON, empresa fica vazia (usuario nao tem customer).
  const [empresa, setEmpresa] = useState(formikInitialValues.empresa || '');
  const [papel, setPapel] = useState(formikInitialValues.papel || '');

  // Sinaliza erro de validacao no select de empresa/papel pos-submit
  // (vermelho na border). Limpa quando o usuario interage.
  const [empresaError, setEmpresaError] = useState(false);
  const [papelError, setPapelError] = useState(false);

  // Nova senha (opcional). Em branco = nao altera. Quando preenchida,
  // valida tamanho minimo e conferencia antes de enviar.
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [senhaError, setSenhaError] = useState('');
  const MIN_SENHA = 8;

  // Estilo do user-form antigo: ao submeter chama setConfirmationModal
  // (parent abre confirm de edicao) e, se ok, submit(values). Para
  // cadastro o parent passa () => {} no setConfirmationModal e
  // submete direto.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (disabled) return;

    // Validacao manual dos selects custom (formik nao os controla
    // diretamente porque sao state proprio).
    const empresaOk = isCompanySelect || !!empresa;
    const papelOk = !!papel;

    if (!empresaOk) setEmpresaError(true);
    if (!papelOk) setPapelError(true);

    if (!empresaOk || !papelOk) return;

    // Valida com os valores atuais dos selects. Antes, papel era mantido
    // apenas no state local e o Formik continuava com papel='', bloqueando o
    // submit mesmo com todos os campos preenchidos.
    const errors = await formik.validateForm({
      ...formik.values,
      empresa,
      papel,
      company: isCompanySelect,
    });
    formik.setTouched({
      name: true,
      email: true,
      empresa: true,
      papel: true,
    });

    if (Object.keys(errors).length) return;

    // Valida nova senha SO quando preenchida (campo opcional). Em branco,
    // mantem a senha atual.
    if (showPasswordFields && (senha || confirmarSenha)) {
      if (senha.length < MIN_SENHA) {
        setSenhaError(`A senha deve ter ao menos ${MIN_SENHA} caracteres.`);
        return;
      }
      if (senha !== confirmarSenha) {
        setSenhaError('As senhas nao conferem.');
        return;
      }
    }

    const selectedEmpresa = parseEmpresaValue(empresa);

    setConfirmationModal(true);
    submit({
      ...formik.values,
      papel,
      empresa: isCompanySelect ? '' : selectedEmpresa.id,
      empresaType: isCompanySelect ? 'company' : selectedEmpresa.type,
      company: isCompanySelect,
      // So envia password quando o admin realmente digitou uma nova.
      ...(showPasswordFields && senha ? { password: senha } : {}),
    });
  };

  const unfilled =
    !formik.values.name ||
    !formik.values.email ||
    !(empresa || isCompanySelect) ||
    !papel;

  // Bloqueio do select de papel: edicao de admin sem company (usuario
  // customer nao pode mudar role de admin).
  const papelDisabled =
    !!(formikInitialValues.papel === 'admin' && !user.company);

  return (
    <s.Scrim
      id={id}
      onClick={(e) => {
        // Click no proprio Scrim (fora do Modal) dispara confirmacao
        // de fechamento, igual ao comportamento antigo do ModalShell.
        if (e.target === e.currentTarget) {
          onCloseConfirmation && onCloseConfirmation(true);
        }
      }}
    >
      <s.Modal>
        <s.ModalHead>
          <s.HeadText>
            <s.Title>{title}</s.Title>
            {!telaConcluido && dadosAbaixo ? (
              <s.Desc>{dadosAbaixo}</s.Desc>
            ) : null}
            {!telaConcluido ? (
              <s.ReqHint>
                <span className="req">*</span>Itens obrigatórios
              </s.ReqHint>
            ) : null}
          </s.HeadText>
          <s.Close
            type="button"
            title="Fechar"
            onClick={() => {
              if (telaConcluido) {
                onClose && onClose();
                setTelaConcluido && setTelaConcluido(false);
              } else {
                onCloseConfirmation && onCloseConfirmation(true);
              }
            }}
          >
            {IClose}
          </s.Close>
        </s.ModalHead>

        <s.ModalBody>
          {telaConcluido ? (
            <s.SuccessWrap>
              {iconFinal ? (
                <s.SuccessIcon src={iconFinal} alt="conclusao" />
              ) : null}
              <s.SuccessText>{sucesso}</s.SuccessText>
            </s.SuccessWrap>
          ) : (
            <s.FormGrid onSubmit={handleSubmit} noValidate>
              {/* Empresa: select de cliente + CheckPill Higra. Quando o
                  CheckPill esta ON, o select fica desabilitado (usuario
                  vinculado a company Higra, sem customer). So aparece
                  o CheckPill se o usuario logado for de uma company
                  (pode marcar outro como interno). */}
              <s.Field>
                <s.Label htmlFor="empresa-select">
                  Empresa <span className="req">*</span>
                </s.Label>
                <s.EmpresaRow>
                  <s.Select
                    id="empresa-select"
                    name="empresa"
                    value={empresa}
                    disabled={isCompanySelect}
                    style={
                      empresaError && !isCompanySelect
                        ? { borderColor: 'var(--cco-red)' }
                        : undefined
                    }
                    onChange={(e) => {
                      setEmpresa(e.target.value);
                      formik.setFieldValue('empresa', e.target.value);
                      setEmpresaError(false);
                    }}
                  >
                    <option value="">Selecione</option>
                    {(currentCustomers || []).map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </s.Select>
                  {user.company ? (
                    <s.CheckPill
                      $on={isCompanySelect}
                      onClick={() => {
                        setIsCompanySelect((v) => !v);
                        setEmpresa('');
                        formik.setFieldValue('empresa', '');
                        setEmpresaError(false);
                      }}
                      title={
                        user.company.name
                          ? `Vincular a ${user.company.name}`
                          : 'Vincular a Higra'
                      }
                    >
                      <s.CheckBox $on={isCompanySelect}>{ICheck}</s.CheckBox>
                      {user.company.name || 'Higra'}
                    </s.CheckPill>
                  ) : null}
                </s.EmpresaRow>
              </s.Field>

              {/* Nome de Usuario */}
              <s.Field>
                <s.Label htmlFor="name">
                  Nome de Usuário <span className="req">*</span>
                </s.Label>
                <s.Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Nome completo"
                  maxLength={40}
                  value={formik.values.name || ''}
                  onChange={(e) => {
                    formik.handleChange(removeInvalidChars(e));
                  }}
                  onBlur={formik.handleBlur}
                  style={
                    formik.touched.name && formik.errors.name
                      ? { borderColor: 'var(--cco-red)' }
                      : undefined
                  }
                />
                {formik.touched.name && formik.errors.name ? (
                  <s.ErrorMsg>{formik.errors.name}</s.ErrorMsg>
                ) : null}
              </s.Field>

              {/* Email */}
              <s.Field>
                <s.Label htmlFor="email">
                  Endereço de E-Mail <span className="req">*</span>
                </s.Label>
                <s.Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="usuario@empresa.com.br"
                  maxLength={50}
                  value={formik.values.email || ''}
                  onChange={(e) => {
                    formik.handleChange(removeInvalidChars(e));
                  }}
                  onBlur={formik.handleBlur}
                  style={
                    formik.touched.email && formik.errors.email
                      ? { borderColor: 'var(--cco-red)' }
                      : undefined
                  }
                />
                {formik.touched.email && formik.errors.email ? (
                  <s.ErrorMsg>{formik.errors.email}</s.ErrorMsg>
                ) : null}
              </s.Field>

              {/* Acesso (Papel) */}
              <s.Field>
                <s.Label htmlFor="papel">
                  Acesso <span className="req">*</span>
                </s.Label>
                <s.Select
                  id="papel"
                  name="papel"
                  value={papel}
                  disabled={papelDisabled}
                  style={
                    papelError
                      ? { borderColor: 'var(--cco-red)' }
                      : undefined
                  }
                  onChange={(e) => {
                    setPapel(e.target.value);
                    formik.setFieldValue('papel', e.target.value);
                    setPapelError(false);
                  }}
                >
                  <option value="">Selecione</option>
                  {/* Mantem o valor atual mesmo se nao estiver na lista
                      filtrada (ex: editando um "higra" sem ele estar
                      em listPapel — evita "sumir" o valor). */}
                  {papel && !listPapel.find((o) => o.value === papel) ? (
                    <option value={papel}>{papel}</option>
                  ) : null}
                  {listPapel.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </s.Select>
              </s.Field>

              {/* Nova senha (opcional) — admin define manualmente. Em branco
                  mantem a senha atual. */}
              {showPasswordFields ? (
                <>
                  <s.Field>
                    <s.Label htmlFor="senha">Nova senha</s.Label>
                    <s.Input
                      id="senha"
                      name="senha"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Deixe em branco para manter a atual"
                      maxLength={64}
                      value={senha}
                      onChange={(e) => {
                        setSenha(e.target.value);
                        setSenhaError('');
                      }}
                      style={
                        senhaError
                          ? { borderColor: 'var(--cco-red)' }
                          : undefined
                      }
                    />
                  </s.Field>
                  <s.Field>
                    <s.Label htmlFor="confirmarSenha">
                      Confirmar nova senha
                    </s.Label>
                    <s.Input
                      id="confirmarSenha"
                      name="confirmarSenha"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Repita a nova senha"
                      maxLength={64}
                      value={confirmarSenha}
                      onChange={(e) => {
                        setConfirmarSenha(e.target.value);
                        setSenhaError('');
                      }}
                      style={
                        senhaError
                          ? { borderColor: 'var(--cco-red)' }
                          : undefined
                      }
                    />
                    {senhaError ? (
                      <s.ErrorMsg>{senhaError}</s.ErrorMsg>
                    ) : null}
                  </s.Field>
                </>
              ) : null}
            </s.FormGrid>
          )}
        </s.ModalBody>

        <s.ModalFoot>
          {telaConcluido ? (
            <s.SaveBtn
              type="button"
              onClick={() => {
                onClose && onClose();
                setTelaConcluido && setTelaConcluido(false);
              }}
            >
              CONCLUIR
            </s.SaveBtn>
          ) : (
            <>
              {msgButton ? <s.ConfirmHint>{msgButton}</s.ConfirmHint> : null}
              <s.SaveBtn
                type="button"
                onClick={handleSubmit}
                disabled={disabled || unfilled}
              >
                {labelButton}
              </s.SaveBtn>
            </>
          )}
        </s.ModalFoot>
      </s.Modal>
    </s.Scrim>
  );
};
